import { FlatTreeControl } from '@angular/cdk/tree';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from '@angular/core';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { BehaviorSubject, Observable, of as observableOf } from 'rxjs';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorReportingService } from '../../services/errorReporting/error-reporting.service';
import { BaseComponent } from 'src/app/base/base.component';
import { DataSourceType } from 'src/app/domainObjects/dataSourceType';
import { FileFlatNode, FileNode } from 'src/app/objectModel/fileNode';
import { BrowseFolderRequest } from 'src/app/messaging/browseFolderRequest';
import { DataConnectorType, FtpService } from 'src/app/domainObjects/dataConnector';
import _ from 'lodash';
import { GetDefaultIngestNodePathRequest } from 'src/app/messaging/getDefaultIngestNodePathRequest';
import { GetDefaultParquetPartitionedIngestNodePathRequest } from 'src/app/messaging/getDefaultParquetPartitionedIngestNodePathRequest';
import { ValidateParquetPartitionedIngestNodePathRequest } from 'src/app/messaging/validateParquetPartitionedIngestNodePathRequest';

class ParquetPartitionFolderSystemItemModel {
  id = 0;
  name = '';
  isFolder = false;
  files: ParquetPartitionFolderSystemItemModel[] | null | undefined;

  constructor(id: number, name: string, isFolder: boolean, files: ParquetPartitionFolderSystemItemModel[] | null) {
    this.id = id;
    this.name = name;
    this.isFolder = isFolder;
    this.files = files;
  }
}

@Component({
  selector: 'app-parquetpartition-folder-browser-dialog',
  templateUrl: './parquetpartition-folder-browser-dialog.component.html',
  styleUrl: './parquetpartition-folder-browser-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ParquetpartitionFolderBrowserDialogComponent extends BaseComponent implements OnInit, AfterViewInit {
  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    switch (event.key) {
      case 'Escape':
        this.dialogRef.close();
        break;
      case 'Enter':
        if (this.focusCycle === 'list' && this.focusedNodeIndex >= 0) {
          const node = this.visibleNodes[this.focusedNodeIndex];
          if (node) {
            event.preventDefault();
            if (this.isParquetPartitionFolder(node)) {
              this.onFileSelected(node);
            } else if (node.expandable) {
              this.treeControl.toggle(node);
              this.cdRef.detectChanges();
            } else {
              this.onFileSelected(node);
            }
          }
        } else if (this.focusCycle === 'refresh') {
          event.preventDefault();
          this.onRefreshIngestParquetPartitionFoldersList();
        } else if (this.focusCycle === 'home') {
          event.preventDefault();
          this.onHomeClicked();
        } else if (this.focusCycle === 'cancel') {
          event.preventDefault();
          this.dialogRef.close();
        } else {
          this.onIngestFilePathSubmit();
        }
        break;
      case 'Tab':
        event.preventDefault();
        if (event.shiftKey) {
          this.cycleTabFocusReverse();
        } else {
          this.cycleTabFocus();
        }
        break;
      case 'ArrowDown':
        if (this.focusCycle === 'list') {
          event.preventDefault();
          this.navigateList(1);
        }
        break;
      case 'ArrowUp':
        if (this.focusCycle === 'list') {
          event.preventDefault();
          this.navigateList(-1);
        }
        break;
    }
  }

  private extractParquetRootAndFolder(path: string): { root: string; folderName: string } {
    const match = path.match(/^(.*?[Pp]arquet[\\\/])([^\\\/]+)?/);
    if (!match) { return { root: path, folderName: '' }; }
    return { root: match[1], folderName: match[2] ?? '' };
  }

  private truncateToParquetRoot(path: string): string {
    return this.extractParquetRootAndFolder(path).root;
  }

  private isParquetPartitionFolder(node: FileFlatNode): boolean {
    const path = node?.path ?? '';
    const name = node?.filename ?? '';
    return /[\\\/]?[Pp]arquet[\\\/]/.test(path) || /Subtask_/i.test(name);
  }

  onPathPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData('text') ?? '').trim();
    if (!pasted) { return; }

    // Rule 1: strip filename if path ends with one
    const lastSep = Math.max(pasted.lastIndexOf('\\'), pasted.lastIndexOf('/'));
    const lastSegment = lastSep >= 0 ? pasted.substring(lastSep + 1) : pasted;
    const cleanPath = lastSegment.includes('.') ? pasted.substring(0, lastSep + 1) : pasted;
    this.ingestParquetPartitionFolderPath = this.truncateToParquetRoot(cleanPath);
  }

 dataSourceTypeId?: DataSourceType;
  supportedFileTypes?: string[];
  fileType?: string;
  isUseFtp?: boolean;
  ftpService?: FtpService;
  parquetPartitionFolderPath: string = '';
  ingestParquetPartitionFolderPath: string = '';
  ingestDataConnectorType: number = 0;
  selectedFileNamePath: string = '';
  selectedParquetPartitionFolderNamePath: string = '';
  defaultIngestPath: string = '';
  treeControl: FlatTreeControl<FileFlatNode>;
  treeFlattener: MatTreeFlattener<FileNode, FileFlatNode>;
  dataSource: MatTreeFlatDataSource<FileNode, FileFlatNode>;
  dataConnectorTypeName: string = 'Parquet';
  navigateToEnteredParquetPartitionPath: boolean = false;
  iParquetPartitionedPathValid: boolean = false;
  fullDatasource = [];
  filteredDatasource = [];
  isLoading = new BehaviorSubject<boolean>(false);

  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('pathInput') pathInputRef: ElementRef;
  @ViewChild('refreshBtn') refreshBtnRef: ElementRef;
  @ViewChild('homeBtn') homeBtnRef: ElementRef;
  @ViewChild('fileList') fileListRef: ElementRef;
  @ViewChild('openBtn') openBtnRef: ElementRef;
  @ViewChild('cancelBtn') cancelBtnRef: ElementRef;
  files = [];
  focusedNodeIndex = -1;
  focusCycle: 'input' | 'refresh' | 'home' | 'list' | 'open' | 'cancel' = 'input';

  constructor(
    @Inject(MAT_DIALOG_DATA) public data,
    public dialogRef: MatDialogRef<ParquetpartitionFolderBrowserDialogComponent>,
    private cdRef: ChangeDetectorRef,
    private configService: ConfigService,
    matDialog: MatDialog,
    errorLogger: ErrorReportingService) {

    super(errorLogger, matDialog);

    this.dataSourceTypeId = data.dataSourceTypeId;
    this.supportedFileTypes = data.supportedFileTypes;
    this.fileType = this.supportedFileTypes?.length > 0 ? this.supportedFileTypes[0] : null;
    const rawPath: string = data.ingestParquetPartitionFolderPath ?? '';
    if (rawPath) {
      const lastSep = Math.max(rawPath.lastIndexOf('\\'), rawPath.lastIndexOf('/'));
      const lastSegment = lastSep >= 0 ? rawPath.substring(lastSep + 1) : rawPath;
      const resolved = lastSegment.includes('.') ? rawPath.substring(0, lastSep + 1) : rawPath;
      const { root, folderName } = this.extractParquetRootAndFolder(resolved.replace(/\\\\$/, '\\'));
      this.ingestParquetPartitionFolderPath = root;
      this.parquetPartitionFolderPath = root;
      this.defaultIngestPath = root;
      this.selectedParquetPartitionFolderNamePath = folderName;
    } else {
      this.ingestParquetPartitionFolderPath = '';
      this.parquetPartitionFolderPath = '';
      const dataConnectorTypeName = DataConnectorType[this.ingestDataConnectorType];
      this.invokeDefaultParquetPartitionIngestNodePath(dataConnectorTypeName);
    }

    this.treeFlattener = new MatTreeFlattener(
      this.transformer,
      this._getLevel,
      this._isExpandable,
      this._getChildren);

    this.treeControl = new FlatTreeControl<FileFlatNode>(
      this._getLevel,
      this._isExpandable);
  }

  transformer = (node: FileNode, level: number) => {
    return new FileFlatNode(!!node.children, node.path, node.name, level);
  }

  private _getLevel = (node: FileFlatNode) => node.level;
  private _isExpandable = (node: FileFlatNode) => node.expandable;
  private _getChildren = (node: FileNode): Observable<FileNode[]> =>
    observableOf(node.children)

  hasChild = (_: number, _nodeData: FileFlatNode) => _nodeData.expandable;

  async ngOnInit(): Promise<void> {
    this.invokeBrowseFolder();
  }

  async invokeBrowseFolder(): Promise<void> {
    this.isLoading.next(true);
    const partitionPath = (this.navigateToEnteredParquetPartitionPath) ? this.ingestParquetPartitionFolderPath : this.parquetPartitionFolderPath; 
    try {
      const organizationId = 1;
      const response = await this.configService.browseParquetPartitionFoldersAsync(organizationId, partitionPath);
      //console.log(`Response: ${response.items}`);

      this.fullDatasource = [...response.items]
        .filter((t) => {
          return (t.isFolder && t.children?.length > 0) || !t.isFolder;
        })
        .map((item) => {
          return { ...item, filename: item.name, children: item.children };
        });

      //this.filteredDatasource = this.filterNodeRecursive([...this.fullDatasource]);

      this.dataSource = new MatTreeFlatDataSource(
        this.treeControl,
        this.treeFlattener);

      //console.log("this.filteredDatasource :", [...this.fullDatasource]);
      this.dataSource.data = [...this.fullDatasource];

      if (this.selectedParquetPartitionFolderNamePath) {
        const matchedNode = this.treeControl.dataNodes?.find(
          (node) => node.filename === this.selectedParquetPartitionFolderNamePath
        );
        if (matchedNode) {
          this.selectedNode = matchedNode;
          this.selectedFileNamePath = matchedNode.path;
          this.focusCycle = 'list';
          this.focusedNodeIndex = this.visibleNodes.indexOf(matchedNode);
        }
      }
    } catch (err) {
      this.displayError('Failed to get folders', 'Error', err, (result) => {
        this.dialogRef.close();
      });
    } finally {
      this.isLoading.next(false);
      this.cdRef.detectChanges();
    }
  }

  ngAfterViewInit(): void {

  }

  onFilterChanged(event: any): void {
    this.filteredDatasource = this.filterNodeRecursive([...this.fullDatasource]);
    this.dataSource.data = this.filteredDatasource;
  }

  filterNodeRecursive(nodes: FileNode[]): FileNode[] {
    const items = [];

    for (const x of nodes) {
      const parts = x.name.split('.');
      const ext = parts[parts.length - 1];

      if (this.fileType.endsWith('*')) {
        const extAny = this.fileType.substring(0, this.fileType.length - 1);
        if ('.' + ext.startsWith(extAny) && !x.isFolder) {
          items.push(x);
        }
      }
      else if ('.' + ext === this.fileType && !x.isFolder) {
        items.push(x);
      }

      if (x.children?.length > 0 && x.isFolder) {
        // add filtered children nodes
        const children = this.filterNodeRecursive(x.children);
        if (children.length > 0) {
          // add the folder
          items.push(x);

          x.children = children;
        }
      }
    }

    return items;
  }

  selectedNode: FileFlatNode = null;
  
  onNodeSingleClick(node: FileFlatNode): void {
    this.selectedNode = node;
    this.selectedFileNamePath = node.path;
    const hasContentAfterParquet = /[\\/]Parquet[\\/].+/i.test(node.path);
    if(hasContentAfterParquet == false) {
      this.ingestParquetPartitionFolderPath = node.path;
    }
    this.focusCycle = 'list';
    const idx = this.visibleNodes.indexOf(node);
    if (idx >= 0) this.focusedNodeIndex = idx;
    this.cdRef.detectChanges();
    this.fileListRef?.nativeElement.focus();
  }

  invokeValidateParquetPartitionedIngestNodePath(selectedParquetPartitionFileNamePath: string): void {
    this.selectedFileNamePath = selectedParquetPartitionFileNamePath;
    this.validateParquetPartitionedIngestNodePath().then(() => {
      if (this.iParquetPartitionedPathValid) {
        this.dialogRef.close({ selectedFileName: selectedParquetPartitionFileNamePath });   
      } else {
        this.displayWarning("Selected path is not a valid parquet partitioned folder path. Please select a valid path.", 'Warning', null);
      }
    });
  }

  onIngestFilePathSubmit(): void {
    if (!this.selectedFileNamePath?.trim()) { return; }
    this.invokeValidateParquetPartitionedIngestNodePath(this.selectedFileNamePath.trim());
  }

  onRefreshIngestParquetPartitionFoldersList(): void {
    this.navigateToEnteredParquetPartitionPath = true;
    if (this.ingestParquetPartitionFolderPath?.trim()) {
      this.selectedParquetPartitionFolderNamePath = '';
      this.selectedNode = null;
      this.selectedFileNamePath = '';
      this.focusedNodeIndex = -1;
      this.invokeBrowseFolder();
    } else {
      this.displayWarning("Ingest parquet partition folder path cannot be empty!", 'Warning', null);
    }
  }

  onHomeClicked(): void {
    this.navigateToEnteredParquetPartitionPath = true;
    this.selectedParquetPartitionFolderNamePath = '';
    this.selectedNode = null;
    this.selectedFileNamePath = '';
    this.focusedNodeIndex = -1;
    this.getDefaultParquetPartitionedIngestNodePath(DataConnectorType[this.ingestDataConnectorType]);
  }

  invokeDefaultParquetPartitionIngestNodePath(dataConnectorTypeName: string): void {
    this.getDefaultParquetPartitionedIngestNodePath(dataConnectorTypeName);
  }

   async getDefaultParquetPartitionedIngestNodePath(dataConnectorTypeName: string) {
    try {
      const organizationId = 1;
      const request = new GetDefaultParquetPartitionedIngestNodePathRequest(
        organizationId,
      );
      const response = await this.configService.getDefaultParquetPartitionedIngestNodePath(request);
      this.ingestParquetPartitionFolderPath = response.defaultIngestNodePath;
      this.defaultIngestPath = this.ingestParquetPartitionFolderPath;
      await this.invokeBrowseFolder();
    } catch (err) {
      this.displayError('Failed to get default ingest path', 'Error', err, (result) => {
      });
    } finally {
      this.isLoading.next(false);
    }
  }

  async validateParquetPartitionedIngestNodePath() {
    try {
      const request = new ValidateParquetPartitionedIngestNodePathRequest(
        this.selectedFileNamePath
      );
      const response = await this.configService.validateParquetPartitionedIngestNodePath(request);
      const isValidParquetPartitionedPath = response.isValidParquetPartitionedPath;
      this.iParquetPartitionedPathValid = isValidParquetPartitionedPath;
    } catch (err) {
      this.displayError('Failed to get default ingest path', 'Error', err, (result) => {
      });
    } finally {
      this.isLoading.next(false);
    }
  }

  onFileSelected(node: FileFlatNode): void {
    console.log('you selected ' + node.path + '/' + node.filename);
    this.invokeValidateParquetPartitionedIngestNodePath(node.path);
  }

  get visibleNodes(): FileFlatNode[] {
    if (!this.treeControl?.dataNodes) return [];
    const result: FileFlatNode[] = [];
    for (const node of this.treeControl.dataNodes) {
      if (this.isNodeVisible(node)) result.push(node);
    }
    return result;
  }

  private isNodeVisible(node: FileFlatNode): boolean {
    if (node.level === 0) return true;
    const nodes = this.treeControl.dataNodes;
    const idx = nodes.indexOf(node);
    for (let i = idx - 1; i >= 0; i--) {
      if (nodes[i].level < node.level) {
        return this.treeControl.isExpanded(nodes[i]) && this.isNodeVisible(nodes[i]);
      }
    }
    return true;
  }

  isFocusedNode(node: FileFlatNode): boolean {
    if (this.focusCycle !== 'list' || this.focusedNodeIndex < 0) return false;
    return this.visibleNodes[this.focusedNodeIndex] === node;
  }

  private navigateList(direction: number): void {
    const visible = this.visibleNodes;
    if (visible.length === 0) return;
    this.focusedNodeIndex = Math.max(0, Math.min(visible.length - 1, this.focusedNodeIndex + direction));
    this.selectedNode = visible[this.focusedNodeIndex];
    this.selectedFileNamePath = this.selectedNode?.path ?? '';
    this.cdRef.detectChanges();
    setTimeout(() => document.querySelector('.focused-file')?.scrollIntoView({ block: 'nearest' }), 0);
  }

  private cycleTabFocus(): void {
    switch (this.focusCycle) {
      case 'input':
        this.pathInputRef?.nativeElement.blur();
        this.focusCycle = 'refresh';
        this.refreshBtnRef?.nativeElement.focus();
        break;
      case 'refresh':
        this.focusCycle = 'home';
        this.homeBtnRef?.nativeElement.focus();
        break;
      case 'home':
        if (this.visibleNodes.length > 0) {
          this.focusCycle = 'list';
          if (this.focusedNodeIndex < 0) this.focusedNodeIndex = 0;
          this.fileListRef?.nativeElement.focus();
        } else {
          this.focusCycle = 'open';
          this.openBtnRef?.nativeElement.focus();
        }
        break;
      case 'list':
        this.focusCycle = 'open';
        this.openBtnRef?.nativeElement.focus();
        break;
      case 'open':
        this.focusCycle = 'cancel';
        this.cancelBtnRef?.nativeElement.focus();
        break;
      case 'cancel':
        this.focusCycle = 'input';
        this.pathInputRef?.nativeElement.focus();
        break;
    }
    this.cdRef.detectChanges();
  }

  private cycleTabFocusReverse(): void {
    switch (this.focusCycle) {
      case 'input':
        this.pathInputRef?.nativeElement.blur();
        this.focusCycle = 'cancel';
        this.cancelBtnRef?.nativeElement.focus();
        break;
      case 'cancel':
        this.focusCycle = 'open';
        this.openBtnRef?.nativeElement.focus();
        break;
      case 'open':
        if (this.visibleNodes.length > 0) {
          this.focusCycle = 'list';
          if (this.focusedNodeIndex < 0) this.focusedNodeIndex = 0;
          this.fileListRef?.nativeElement.focus();
        } else {
          this.focusCycle = 'home';
          this.homeBtnRef?.nativeElement.focus();
        }
        break;
      case 'list':
        this.focusCycle = 'home';
        this.homeBtnRef?.nativeElement.focus();
        break;
      case 'home':
        this.focusCycle = 'refresh';
        this.refreshBtnRef?.nativeElement.focus();
        break;
      case 'refresh':
        this.focusCycle = 'input';
        this.pathInputRef?.nativeElement.focus();
        break;
    }
    this.cdRef.detectChanges();
  }
}
